import React, { useEffect, useMemo, useState } from "react";

import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Divider,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Snackbar,
  Stack,
  TextField,
  Typography,
} from "@mui/material";

import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import RefreshIcon from "@mui/icons-material/Refresh";
import ClearIcon from "@mui/icons-material/Clear";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import SearchOffIcon from "@mui/icons-material/SearchOff";

import { getRole } from "../../helper";
import FactoryStockService from "../../services/FactoryStockService";


// =====================================================
// COMPONENT
// =====================================================

const DeviceFeatureAssignment = () => {
  const role = getRole();

  const normalizedRole = role
    ?.toString()
    .trim()
    .toLowerCase();

  const isFactoryManager =
    normalizedRole === "factorymanager";


  // =====================================================
  // FILTER OPTIONS
  // =====================================================

  const [deviceFeatures, setDeviceFeatures] = useState([]);

  const [manufacturers, setManufacturers] = useState([]);

  const [loadingOptions, setLoadingOptions] =
    useState(false);


  // =====================================================
  // FORM
  // =====================================================

  const [selectedFeature, setSelectedFeature] =
    useState("");

  const [imeis, setImeis] = useState("");

  const [assigning, setAssigning] =
    useState(false);


  // =====================================================
  // RESULT
  // =====================================================

  const [result, setResult] = useState(null);


  // =====================================================
  // SNACKBAR
  // =====================================================

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });


  // =====================================================
  // SNACKBAR HELPER
  // =====================================================

  const showSnackbar = (
    message,
    severity = "success"
  ) => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };


  const closeSnackbar = () => {
    setSnackbar((prev) => ({
      ...prev,
      open: false,
    }));
  };


  // =====================================================
  // API ERROR
  // =====================================================

  const getApiErrorMessage = (
    error,
    fallback = "Something went wrong."
  ) => {
    const data = error?.response?.data;

    if (!data) {
      return fallback;
    }

    if (typeof data === "string") {
      return data;
    }

    if (data.error) {
      return data.error;
    }

    if (data.detail) {
      return data.detail;
    }

    if (typeof data === "object") {
      const messages = [];

      Object.entries(data).forEach(
        ([field, value]) => {
          if (Array.isArray(value)) {
            messages.push(
              `${field}: ${value.join(", ")}`
            );
          } else if (
            typeof value === "string"
          ) {
            messages.push(
              `${field}: ${value}`
            );
          }
        }
      );

      if (messages.length > 0) {
        return messages.join(" | ");
      }
    }

    return fallback;
  };


  // =====================================================
  // LOAD FILTER OPTIONS
  // =====================================================

  const loadFilterOptions = async () => {
    setLoadingOptions(true);

    try {
      const response =
        await FactoryStockService.deviceFeatureFilterOptions(
          {}
        );

      const data = response?.data || {};

      setDeviceFeatures(
        Array.isArray(data.device_features)
          ? data.device_features
          : []
      );

      setManufacturers(
        Array.isArray(data.manufacturers)
          ? data.manufacturers
          : []
      );

    } catch (error) {
      console.error(
        "Device feature filter options error:",
        error
      );

      showSnackbar(
        getApiErrorMessage(
          error,
          "Failed to load device features."
        ),
        "error"
      );

      setDeviceFeatures([]);
      setManufacturers([]);
    } finally {
      setLoadingOptions(false);
    }
  };


  // =====================================================
  // LOAD OPTIONS ON PAGE LOAD
  // =====================================================

  useEffect(() => {
    if (isFactoryManager) {
      loadFilterOptions();
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);


  // =====================================================
  // PARSE IMEIS
  // =====================================================

  const parsedImeis = useMemo(() => {
    if (!imeis.trim()) {
      return [];
    }

    return imeis
      .split(/[\s,]+/)
      .map((imei) => imei.trim())
      .filter(Boolean);
  }, [imeis]);


  // =====================================================
  // VALID IMEIS
  // =====================================================

  const validImeis = useMemo(() => {
    return parsedImeis.filter((imei) =>
      /^\d{15}$/.test(imei)
    );
  }, [parsedImeis]);


  // =====================================================
  // INVALID IMEIS
  // =====================================================

  const invalidImeis = useMemo(() => {
    return parsedImeis.filter(
      (imei) => !/^\d{15}$/.test(imei)
    );
  }, [parsedImeis]);


  // =====================================================
  // REMOVE DUPLICATE IMEIS
  // =====================================================

  const uniqueImeis = useMemo(() => {
    return [...new Set(validImeis)];
  }, [validImeis]);


  // =====================================================
  // ASSIGN FEATURE
  // =====================================================

  const handleAssign = async () => {
    if (!selectedFeature) {
      showSnackbar(
        "Please select a device feature.",
        "error"
      );

      return;
    }

    if (!imeis.trim()) {
      showSnackbar(
        "Please paste at least one IMEI.",
        "error"
      );

      return;
    }

    if (uniqueImeis.length === 0) {
      showSnackbar(
        "No valid 15-digit IMEI found.",
        "error"
      );

      return;
    }

    setAssigning(true);
    setResult(null);

    try {
      const response =
        await FactoryStockService.assignDeviceFeature(
          {
            feature_id: Number(selectedFeature),

            // Send the pasted block as-is.
            // Backend supports newline/comma/whitespace.
            imeis: imeis.trim(),
          }
        );

      const data = response?.data || {};

      setResult(data);

      showSnackbar(
        data.message ||
          "Device feature assignment completed.",
        "success"
      );

    } catch (error) {
      console.error(
        "Device feature assignment error:",
        error
      );

      /*
       * Backend may return invalid_imeis along
       * with the 400 error.
       */
      const errorData =
        error?.response?.data;

      if (
        errorData &&
        (
          errorData.invalid_imeis ||
          errorData.not_found_imeis ||
          errorData.assigned !== undefined
        )
      ) {
        setResult(errorData);
      }

      showSnackbar(
        getApiErrorMessage(
          error,
          "Failed to assign device feature."
        ),
        "error"
      );
    } finally {
      setAssigning(false);
    }
  };


  // =====================================================
  // CLEAR FORM
  // =====================================================

  const handleClear = () => {
    setSelectedFeature("");
    setImeis("");
    setResult(null);
  };


  // =====================================================
  // SELECTED FEATURE NAME
  // =====================================================

  const selectedFeatureName =
    deviceFeatures.find(
      (feature) =>
        String(feature.id) ===
        String(selectedFeature)
    )?.name || "";


  // =====================================================
  // ACCESS CHECK
  // =====================================================

  if (!isFactoryManager) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error">
          Only factorymanager can access this
          page.
        </Alert>
      </Box>
    );
  }


  // =====================================================
  // RENDER
  // =====================================================

  return (
    <Box
      sx={{
        width: "100%",
        p: 2,
      }}
    >
      <Paper
        elevation={1}
        sx={{
          borderRadius: 2,
          overflow: "hidden",
        }}
      >

        {/* =================================================
            HEADER
        ================================================= */}

        <Box
          sx={{
            px: 2.5,
            py: 2,
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            gap: 2,
            flexWrap: "wrap",
          }}
        >
          <Box>
            <Typography
              variant="h6"
              fontWeight={600}
            >
              Device Feature Assignment
            </Typography>

            <Typography
              variant="body2"
              color="text.secondary"
              sx={{ mt: 0.5 }}
            >
              Bulk assign a device feature to
              multiple IMEIs.
            </Typography>
          </Box>

        </Box>

        <Divider />


        {/* =================================================
            FORM
        ================================================= */}

        <Box sx={{ p: 2.5 }}>

          <Stack spacing={3}>

            {/* FEATURE SELECT */}

            <FormControl
              fullWidth
              size="small"
              disabled={
                loadingOptions ||
                assigning
              }
            >
              <InputLabel>
                Device Feature
              </InputLabel>

              <Select
                value={selectedFeature}
                label="Device Feature"
                onChange={(event) =>
                  setSelectedFeature(
                    event.target.value
                  )
                }
              >
                {deviceFeatures.length ===
                0 ? (
                  <MenuItem disabled>
                    No active device features
                    available
                  </MenuItem>
                ) : (
                  deviceFeatures.map(
                    (feature) => (
                      <MenuItem
                        key={feature.id}
                        value={feature.id}
                      >
                        {feature.name}
                      </MenuItem>
                    )
                  )
                )}
              </Select>
            </FormControl>


            {/* REFRESH OPTIONS */}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                mt: -2,
              }}
            >
              <Button
                size="small"
                startIcon={
                  loadingOptions ? (
                    <CircularProgress
                      size={16}
                    />
                  ) : (
                    <RefreshIcon />
                  )
                }
                onClick={loadFilterOptions}
                disabled={
                  loadingOptions ||
                  assigning
                }
              >
                Refresh Features
              </Button>
            </Box>


            {/* IMEI INPUT */}

            <TextField
              fullWidth
              multiline
              minRows={10}
              maxRows={18}
              label="IMEIs"
              placeholder={
                "Paste IMEIs here...\n\n" +
                "Example:\n" +
                "866192070567043\n" +
                "866192070567044\n" +
                "866192070567045"
              }
              value={imeis}
              onChange={(event) =>
                setImeis(
                  event.target.value
                )
              }
              disabled={assigning}
            />


            {/* IMEI COUNTS */}

            <Box
              sx={{
                display: "flex",
                gap: 1,
                flexWrap: "wrap",
              }}
            >
              <Chip
                size="small"
                label={`Total: ${parsedImeis.length}`}
              />

              <Chip
                size="small"
                color="success"
                variant="outlined"
                label={`Valid: ${uniqueImeis.length}`}
              />

              {invalidImeis.length >
                0 && (
                <Chip
                  size="small"
                  color="error"
                  variant="outlined"
                  label={`Invalid: ${invalidImeis.length}`}
                />
              )}
            </Box>


            {/* ACTION BUTTONS */}

            <Box
              sx={{
                display: "flex",
                justifyContent: "flex-end",
                gap: 1.5,
              }}
            >
              <Button
                variant="outlined"
                startIcon={<ClearIcon />}
                onClick={handleClear}
                disabled={
                  assigning ||
                  (
                    !imeis &&
                    !selectedFeature
                  )
                }
              >
                Clear
              </Button>

              <Button
                variant="contained"
                startIcon={
                  assigning ? (
                    <CircularProgress
                      size={18}
                      color="inherit"
                    />
                  ) : (
                    <AssignmentTurnedInIcon />
                  )
                }
                onClick={handleAssign}
                disabled={
                  assigning ||
                  !selectedFeature ||
                  uniqueImeis.length === 0
                }
              >
                {assigning
                  ? "Assigning..."
                  : "Assign Feature"}
              </Button>
            </Box>

          </Stack>
        </Box>


        {/* =================================================
            RESULT
        ================================================= */}

        {result && (
          <>
            <Divider />

            <Box sx={{ p: 2.5 }}>

              <Typography
                variant="subtitle1"
                fontWeight={600}
                sx={{ mb: 2 }}
              >
                Assignment Result
              </Typography>


              {/* MESSAGE */}

              {result.message && (
                <Alert
                  severity={
                    Number(result.assigned) >
                    0
                      ? "success"
                      : "info"
                  }
                  sx={{ mb: 2 }}
                >
                  {result.message}
                </Alert>
              )}


              {/* SUMMARY */}

              <Box
                sx={{
                  display: "grid",
                  gridTemplateColumns:
                    "repeat(auto-fit, minmax(180px, 1fr))",
                  gap: 2,
                  mb: 2,
                }}
              >

                <Paper
                  variant="outlined"
                  sx={{ p: 2 }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Assigned
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={600}
                    color="success.main"
                  >
                    {result.assigned ??
                      0}
                  </Typography>
                </Paper>


                <Paper
                  variant="outlined"
                  sx={{ p: 2 }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Invalid IMEIs
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={600}
                    color={
                      result.invalid_imeis
                        ?.length
                        ? "error.main"
                        : "text.primary"
                    }
                  >
                    {result.invalid_imeis
                      ?.length || 0}
                  </Typography>
                </Paper>


                <Paper
                  variant="outlined"
                  sx={{ p: 2 }}
                >
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Not Found
                  </Typography>

                  <Typography
                    variant="h5"
                    fontWeight={600}
                    color={
                      result.not_found_imeis
                        ?.length
                        ? "warning.main"
                        : "text.primary"
                    }
                  >
                    {result.not_found_imeis
                      ?.length || 0}
                  </Typography>
                </Paper>

              </Box>


              {/* FEATURE */}

              {(result.feature ||
                selectedFeatureName) && (
                <Box sx={{ mb: 2 }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                  >
                    Assigned Feature
                  </Typography>

                  <Typography
                    fontWeight={600}
                  >
                    {result.feature?.name ||
                      selectedFeatureName}
                  </Typography>
                </Box>
              )}


              {/* INVALID IMEIS */}

              {result.invalid_imeis?.length >
                0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    mb: 2,
                    borderColor:
                      "error.light",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <ErrorOutlineIcon
                      color="error"
                      fontSize="small"
                    />

                    <Typography
                      fontWeight={600}
                    >
                      Invalid IMEIs
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {result.invalid_imeis.map(
                      (imei, index) => (
                        <Chip
                          key={`${imei}-${index}`}
                          label={imei}
                          size="small"
                          color="error"
                          variant="outlined"
                        />
                      )
                    )}
                  </Box>
                </Paper>
              )}


              {/* NOT FOUND IMEIS */}

              {result.not_found_imeis
                ?.length > 0 && (
                <Paper
                  variant="outlined"
                  sx={{
                    p: 2,
                    borderColor:
                      "warning.light",
                  }}
                >
                  <Stack
                    direction="row"
                    spacing={1}
                    alignItems="center"
                    sx={{ mb: 1 }}
                  >
                    <SearchOffIcon
                      color="warning"
                      fontSize="small"
                    />

                    <Typography
                      fontWeight={600}
                    >
                      IMEIs Not Found
                    </Typography>
                  </Stack>

                  <Box
                    sx={{
                      display: "flex",
                      gap: 1,
                      flexWrap: "wrap",
                    }}
                  >
                    {result.not_found_imeis.map(
                      (imei, index) => (
                        <Chip
                          key={`${imei}-${index}`}
                          label={imei}
                          size="small"
                          color="warning"
                          variant="outlined"
                        />
                      )
                    )}
                  </Box>
                </Paper>
              )}

            </Box>
          </>
        )}

      </Paper>


      {/* =================================================
          SNACKBAR
      ================================================= */}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={5000}
        onClose={closeSnackbar}
        anchorOrigin={{
          vertical: "top",
          horizontal: "right",
        }}
      >
        <Alert
          onClose={closeSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>

    </Box>
  );
};

export default DeviceFeatureAssignment;